import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useUnLikePromotionReplyMutation } from 'queries/promotions/mutations/useUnLikePromotionReplyMutation';
import { useLikePromotionReplyMutation } from '@/queries/promotions/mutations/useLikePromoyionReplyMutation';
import { promotionsQueryKey } from '@/queries/promotions/queryKey';

export const usePromotionReplyLike = ({
  promotionId,
  replyId,
  isLiked,
  likeCount: prevLikeCount,
}: {
  promotionId: number;
  replyId: string;
  isLiked: boolean;
  likeCount: number;
}) => {
  const [liked, setLiked] = useState(isLiked);
  const [likeCount, setLikeCount] = useState(prevLikeCount);
  const { tracker } = useTrackingState();
  const queryClient = useQueryClient();

  const likeReply = useCallback(() => {
    setLiked(true);
    setLikeCount(prev => prev + 1);
  }, []);

  const unlikeReply = useCallback(() => {
    setLiked(false);
    setLikeCount(prev => prev - 1);
  }, []);

  const { mutate: mutateLike } = useLikePromotionReplyMutation(promotionId, replyId, {
    onMutate: () => likeReply(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promotionsQueryKey.promotionReplyLikes }).then();
      tracker.track(TrackingEventType.LikePromotionReply, { replyId });
    },
    onError: () => unlikeReply(),
  });
  const { mutate: mutateUnlike } = useUnLikePromotionReplyMutation(promotionId, replyId, {
    onMutate: () => unlikeReply(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promotionsQueryKey.promotionReplyLikes }).then();
      tracker.track(TrackingEventType.UnlikePromotionReply, { replyId });
    },
    onError: () => likeReply(),
  });

  useEffect(() => {
    setLiked(isLiked);
  }, [isLiked]);

  useEffect(() => {
    setLikeCount(prevLikeCount);
  }, [prevLikeCount]);

  const toggleLike = useCallback(() => {
    if (liked) {
      mutateUnlike();
      return;
    }
    mutateLike();
  }, [liked, mutateLike, mutateUnlike]);

  return {
    liked,
    likeCount,
    toggleLike,
  };
};
